/**
 * PotatoPiratesActivity — lesson activity component (2-player, no target selectors).
 *
 * The student programs their own 2 ships using drag-and-drop cards. No target
 * selector is shown — attacks auto-resolve to the first living enemy ship.
 * The opponent fleet is fixed and shown read-only for context.
 * Resolution uses runSimulation (student vs opponent, single pass).
 */
import React from 'react'
import { ChevronUp, ChevronDown, Loader2, Play, RotateCcw } from 'lucide-react'
import {
  cloneState,
  runSimulation,
  makeSinglePlayerState,
  type Card,
  type GameState,
  type SimEvent,
  type CardType,
} from './engine'
import { CardPalette } from './CardPalette'
import { ShipBoard } from './ShipBoard'
import { ModelAnswerEditor } from './ModelAnswerEditor'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActivityData {
  question?: string
  hints?: string[]
  player_ships?: number[]
  opponent_ships?: number[]
  allowedCards?: string[]
  objective?: string
  /** Teacher-configured model solution: one Card[] per player ship. */
  model_answer?: Card[][]
}

interface PotatoPiratesActivityProps {
  activity: { id?: string; activity_data: ActivityData; title?: string; [key: string]: any }
  onSubmit: (responseData: Record<string, any>) => void
  onComplete?: () => void | Promise<void>
  disabled?: boolean
  hideSubmit?: boolean
}

interface ActivityResult {
  won: boolean
  potatoesDestroyed: number
}

function serializePrograms(state: GameState) {
  return state.fleets[0].ships.map((s) => ({ ship: s.name, program: s.program }))
}

// ---------------------------------------------------------------------------
// Compact read-only opponent fleet display
// ---------------------------------------------------------------------------
function OpponentFleet({ ships, snapshot }: {
  ships: { id: string; name: string; potatoes: number; maxPotatoes: number }[]
  snapshot: Record<string, number> | null
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {ships.map((ship) => {
        const current = snapshot && ship.id in snapshot ? snapshot[ship.id] : ship.potatoes
        const dead = current <= 0
        return (
          <div key={ship.id} style={{
            flex: '1 1 140px', border: '2px solid var(--color-danger)', borderRadius: 12,
            padding: '10px 12px', background: dead ? 'var(--pp-surface-slot)' : 'var(--color-danger-subtle)', opacity: dead ? 0.6 : 1,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 18 }}>{dead ? '🌊' : '⛵'}</span>
              <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--pp-text)' }}>{ship.name}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 12, color: dead ? 'var(--pp-text-muted)' : 'var(--color-warning)' }}>
                {current}/{ship.maxPotatoes} 🥔
              </span>
            </div>
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {Array.from({ length: ship.maxPotatoes }).map((_, i) => (
                <span key={i} style={{
                  width: 11, height: 11, borderRadius: '50%',
                  background: i < current ? 'var(--color-danger)' : 'var(--pp-border)',
                  transition: 'background 150ms ease',
                }} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Core battle view (no target selectors)
// ---------------------------------------------------------------------------
function ActivityBattleView({ initialState, allowedCards, disabled, onResult, onBeforeRun, stepMs = 600 }: {
  initialState: GameState
  allowedCards?: string[]
  disabled: boolean
  onResult: (result: ActivityResult) => void
  /** When provided: called with the computed result before animation starts.
   *  The button becomes "Submit Solution" and Reset is hidden (one-shot). */
  onBeforeRun?: (result: ActivityResult, state: GameState) => Promise<void>
  stepMs?: number
}) {
  const [editState, setEditState] = React.useState<GameState>(() => cloneState(initialState))
  const [events, setEvents] = React.useState<SimEvent[]>([])
  const [step, setStep] = React.useState(-1)
  const [running, setRunning] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const [isPreRunning, setIsPreRunning] = React.useState(false)
  const [battleResult, setBattleResult] = React.useState<ActivityResult | null>(null)
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const reportedRef = React.useRef(false)

  const playerFleet = editState.fleets[0]
  const opponentFleet = editState.fleets[1]
  const locked = disabled || running || done || isPreRunning

  // Animated replay driver.
  React.useEffect(() => {
    if (!running) return
    if (step >= events.length - 1) { setRunning(false); return }
    timerRef.current = setTimeout(() => setStep((s) => s + 1), stepMs)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [running, step, events.length, stepMs])

  // Fire onResult when replay finishes.
  React.useEffect(() => {
    if (!running && battleResult && !reportedRef.current && step === events.length - 1 && events.length > 0) {
      reportedRef.current = true
      setDone(true)
      onResult(battleResult)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, battleResult, step, events.length])

  const handleRun = async () => {
    const res = runSimulation(editState, playerFleet.id, opponentFleet.id)
    const ar: ActivityResult = { won: res.attackerWon, potatoesDestroyed: res.potatoesDestroyed }
    setBattleResult(ar)
    reportedRef.current = false

    if (onBeforeRun) {
      setIsPreRunning(true)
      await onBeforeRun(ar, editState)
      setIsPreRunning(false)
    }

    if (res.events.length === 0) {
      setStep(-1); setRunning(false); setDone(true)
      reportedRef.current = true
      onResult(ar)
      return
    }
    setEvents(res.events)
    setStep(0)
    setRunning(true)
  }

  const handleReset = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setEvents([]); setStep(-1); setRunning(false); setDone(false)
    setBattleResult(null); reportedRef.current = false
  }

  const updateShipProgram = (shipId: string, program: Card[]) => {
    setEditState((prev) => ({
      fleets: prev.fleets.map((f) =>
        f.id !== playerFleet.id ? f : { ...f, ships: f.ships.map((s) => (s.id === shipId ? { ...s, program } : s)) },
      ),
    }))
  }

  const currentEvent = step >= 0 ? events[step] : null
  const snapshot = currentEvent?.snapshot ?? null
  const potatoesOf = (id: string, fb: number) => (snapshot && id in snapshot ? snapshot[id] : fb)

  const highlightShipId = currentEvent?.targetShipId ?? null
  const sourceShipId = currentEvent?.sourceShipId ?? null
  const highlightCardId = currentEvent?.sourceCardId ?? null

  const submitMode = !!onBeforeRun

  // Battle focus: once the player submits and the attack runs, collapse the card
  // palette and zoom into the fleets to draw focus to the battle. Reverses when done.
  const focused = isPreRunning || running

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: focused ? 0 : 16, alignItems: 'flex-start', flexWrap: 'wrap', transition: 'gap 400ms ease' }}>
        {/* Card palette — collapses out of view while the battle runs */}
        <div style={{
          width: focused ? 0 : 200, flexShrink: 0, position: 'sticky', top: 8,
          background: 'var(--pp-surface)',
          border: focused ? '1px solid transparent' : '1px solid var(--pp-border)',
          borderRadius: 14, padding: focused ? 0 : 12,
          opacity: focused ? 0 : 1, overflow: 'hidden',
          pointerEvents: focused ? 'none' : 'auto',
          transition: 'width 400ms cubic-bezier(0.4,0,0.2,1), padding 400ms ease, opacity 250ms ease, border-color 250ms ease',
        }}>
          <CardPalette allowedCards={allowedCards} activityMode />
        </div>

        {/* Battlefield — zooms toward center while the battle runs */}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16,
          transform: focused ? 'scale(1.08)' : 'scale(1)',
          transformOrigin: 'center center',
          margin: focused ? '28px 0' : '0',
          willChange: 'transform',
          transition: 'transform 450ms cubic-bezier(0.34,1.56,0.64,1), margin 450ms ease',
        }}>
          {/* Your fleet */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--color-secondary)' }} />
              <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--color-text)' }}>Your Fleet</span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {playerFleet.ships.map((ship) => (
                <div key={ship.id} style={{ flex: '1 1 240px' }}>
                  <ShipBoard
                    ship={{ ...ship, potatoes: potatoesOf(ship.id, ship.potatoes) }}
                    editable={!locked}
                    accent="#0ea5e9"
                    targets={[]}
                    onProgramChange={(program) => updateShipProgram(ship.id, program)}
                    highlightShipId={highlightShipId === ship.id || sourceShipId === ship.id ? ship.id : null}
                    highlightCardId={sourceShipId === ship.id ? highlightCardId : null}
                    allowParamEdit={false}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Opponent fleet */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--color-danger)' }} />
              <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--color-text)' }}>Enemy Fleet</span>
            </div>
            <OpponentFleet ships={opponentFleet.ships} snapshot={snapshot} />
          </div>
        </div>
      </div>

      {/* Controls */}
      {!done && (
        submitMode ? (
          <button
            onClick={handleRun}
            disabled={locked}
            className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-on-primary)] flex items-center justify-center gap-2"
            style={{ background: 'var(--gradient-primary)' }}
          >
            {isPreRunning
              ? <><Loader2 size={15} className="animate-spin" /> Submitting…</>
              : running
                ? <><Play size={15} /> Running…</>
                : <><Play size={15} /> Submit Solution</>
            }
          </button>
        ) : !disabled ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleRun} disabled={locked}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
                borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 13,
                cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.5 : 1,
                background: 'var(--gradient-primary)', color: 'var(--color-on-primary)',
              }}
            >
              <Play size={15} /> Run Battle
            </button>
            {(battleResult || running) && (
              <button
                onClick={handleReset} disabled={running}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px',
                  borderRadius: 10, border: '1px solid var(--color-border)', fontWeight: 700, fontSize: 13,
                  cursor: running ? 'not-allowed' : 'pointer', opacity: running ? 0.5 : 1,
                  background: 'var(--color-surface)', color: 'var(--color-text)',
                }}
              >
                <RotateCcw size={14} /> Reset
              </button>
            )}
          </div>
        ) : null
      )}

      {/* Event ticker */}
      {currentEvent && (
        <div style={{
          padding: '9px 12px', borderRadius: 10, background: 'var(--pp-ticker-bg)', color: 'var(--pp-ticker-text)',
          fontSize: 13, fontFamily: 'ui-monospace, monospace',
        }}>
          {currentEvent.message}
        </div>
      )}

      {/* Result banner */}
      {battleResult && !running && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 12,
          background: battleResult.won ? 'var(--color-success-subtle)' : 'var(--color-danger-subtle)',
          color: battleResult.won ? 'var(--color-success)' : 'var(--color-danger)',
          fontWeight: 800, fontSize: 14,
        }}>
          <span>{battleResult.won ? 'Victory! Enemy fleet destroyed. 🏴‍☠️' : 'Not quite — enemy fleet survived.'}</span>
          <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 12 }}>
            {battleResult.potatoesDestroyed} potatoes destroyed
          </span>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lesson activity wrapper (submit / wait / complete screens)
// ---------------------------------------------------------------------------
export function PotatoPiratesActivity({
  activity, onSubmit, onComplete, disabled = false, hideSubmit = false,
}: PotatoPiratesActivityProps) {
  const data = activity.activity_data || {}
  const playerShips = data.player_ships?.length ? data.player_ships : [10, 10]
  const opponentShips = data.opponent_ships?.length ? data.opponent_ships : [10, 10]
  const hints = data.hints || []

  const [initialState] = React.useState<GameState>(() => makeSinglePlayerState(opponentShips, playerShips))
  const [activityResult, setActivityResult] = React.useState<ActivityResult | null>(null)
  const [localSubmitted, setLocalSubmitted] = React.useState(false)
  const [hintsOpen, setHintsOpen] = React.useState(false)
  const startTimeRef = React.useRef<number>(Date.now())

  const hasModelAnswer = data.model_answer && Array.isArray(data.model_answer) &&
    data.model_answer.some((p: Card[]) => p.length > 0)

  // Disabled externally and nothing submitted this session.
  if (!hideSubmit && disabled && !localSubmitted) {
    // Student already responded in a previous render (activity ended and component remounted):
    // keep showing the model answer so they can review while waiting for the teacher.
    if (activity.player_has_responded) {
      return (
        <div className="space-y-5">
          <div className="px-5 py-4">
            <p className="text-sm font-semibold text-[var(--color-primary)] leading-relaxed whitespace-pre-wrap">
              {data.question || 'Program your ships to destroy the enemy fleet!'}
            </p>
          </div>
          {hasModelAnswer && (
            <div className="px-2 space-y-2">
              <div className="flex items-center gap-2 px-1">
                <span className="text-sm font-bold text-[var(--color-text)]">✅ Model Answer</span>
                <span className="text-xs text-[var(--color-text-muted)]">— the intended solution for this challenge</span>
              </div>
              <ModelAnswerEditor
                programs={data.model_answer ?? []}
                shipCount={playerShips.length}
                allowedCards={data.allowedCards}
                readOnly
              />
            </div>
          )}
          {onComplete && (
            <div className="px-2">
              <button
                onClick={() => onComplete()}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 text-[var(--color-on-primary)]"
                style={{ background: 'var(--gradient-primary)' }}
              >
                See my results →
              </button>
            </div>
          )}
        </div>
      )
    }
    // Activity locked but student hasn't responded: show waiting screen.
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center text-3xl">🏴‍☠️</div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">Your captain will start the next activity soon.</p>
      </div>
    )
  }

  // Called by ActivityBattleView BEFORE the animation starts: submit result to server.
  const handleBeforeRun = async (result: ActivityResult, state: GameState) => {
    const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000)
    await onSubmit({
      outcome: result.won ? 'win' : 'lose',
      won: result.won,
      potatoes_destroyed: result.potatoesDestroyed,
      programs: serializePrograms(state),
      time_taken: timeTaken,
    })
    setLocalSubmitted(true)
  }

  return (
    <div className="space-y-5">
      {/* Question */}
      <div className="px-5 py-4">
        <p className="text-sm font-semibold text-[var(--color-primary)] leading-relaxed whitespace-pre-wrap">
          {data.question || 'Program your ships to destroy the enemy fleet!'}
        </p>
        {data.objective && (
          <p className="text-xs text-theme-text-muted mt-2 whitespace-pre-wrap">{data.objective}</p>
        )}
      </div>

      {/* Hints */}
      {hints.length > 0 && (
        <div className="px-5">
          <button
            onClick={() => setHintsOpen((o) => !o)}
            className="flex items-center gap-1 bg-transparent border-none p-0 text-xs text-[var(--color-primary)] cursor-pointer"
          >
            {hintsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {hintsOpen ? 'Hide hint' : 'Show hint'}
          </button>
          {hintsOpen && (
            <p className="mt-1 text-xs text-theme-text-muted italic bg-[var(--color-surface-hover)] rounded px-2 py-1">
              {hints[0]}
            </p>
          )}
        </div>
      )}

      {/* Battle view */}
      <div className="px-2">
        <ActivityBattleView
          initialState={initialState}
          allowedCards={data.allowedCards}
          disabled={disabled}
          onBeforeRun={!hideSubmit ? handleBeforeRun : undefined}
          onResult={(res) => setActivityResult(res)}
        />
      </div>

      {/* Model Answer + See my results — revealed after battle completes */}
      {activityResult !== null && (
        <>
          {hasModelAnswer && (
            <div className="px-2 space-y-2">
              <div className="flex items-center gap-2 px-1">
                <span className="text-sm font-bold text-[var(--color-text)]">✅ Model Answer</span>
                <span className="text-xs text-[var(--color-text-muted)]">— the intended solution for this challenge</span>
              </div>
              <ModelAnswerEditor
                programs={data.model_answer ?? []}
                shipCount={playerShips.length}
                allowedCards={data.allowedCards}
                readOnly
              />
            </div>
          )}

          {!hideSubmit && onComplete && (
            <div className="px-2">
              <button
                onClick={() => onComplete()}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 text-[var(--color-on-primary)]"
                style={{ background: 'var(--gradient-primary)' }}
              >
                See my results →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
